import React from 'react';
import { ExternalLink, FileText, Play } from 'lucide-react';
import { cn } from '../ui/utils';
import { Tooltip } from '../ui/legacy-forms';

const PAST_DUE_TOOLTIP = 'The due date for this assignment has passed.';
const HIDDEN_SOLUTIONS_TOOLTIP = 'Solutions are locked for this assignment.';

export function getActivityAction(activity) {
  const isOffline = Boolean(activity?.isOffline || activity?.pdfUrl);

  if (isOffline) {
    return {
      variant: 'view',
      label: 'View Submission',
      Icon: FileText,
      enabled: Boolean(activity?.pdfUrl)
    };
  }

  if (activity?.canView) {
    const canReveal = activity.revealSolutions !== false;
    return {
      variant: 'view',
      label: 'View Submission',
      Icon: ExternalLink,
      enabled: canReveal,
      disabledReason: canReveal ? null : HIDDEN_SOLUTIONS_TOOLTIP
    };
  }

  if (activity?.canResume) {
    const isPastDue = Boolean(activity?.isDueDatePassed);
    return {
      variant: 'continue',
      label: 'Continue',
      Icon: Play,
      enabled: !isPastDue,
      disabledReason: isPastDue ? PAST_DUE_TOOLTIP : null
    };
  }

  return {
    variant: 'view',
    label: 'View Submission',
    Icon: ExternalLink,
    enabled: false
  };
}

function ActivityActionButton({ activity, onView }) {
  const action = getActivityAction(activity);
  const Icon = action.Icon;
  const isContinue = action.variant === 'continue';

  const button = (
    <button
      type="button"
      disabled={!action.enabled}
      title={action.disabledReason || undefined}
      onClick={(event) => {
        event.stopPropagation();
        if (action.enabled) onView(activity);
      }}
      className={cn(
        'inline-flex w-40 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
        !action.enabled && 'pointer-events-none cursor-not-allowed bg-gray-100 text-gray-400',
        action.enabled &&
          isContinue &&
          'bg-primary text-white shadow-sm hover:bg-primary/90',
        action.enabled &&
          !isContinue &&
          'border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'
      )}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      {action.label}
    </button>
  );

  if (!action.enabled && action.disabledReason) {
    return (
      <Tooltip label={action.disabledReason}>
        <span className="inline-flex w-40 cursor-not-allowed">{button}</span>
      </Tooltip>
    );
  }

  return button;
}

export default ActivityActionButton;
